import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Logo } from './Logo';

interface DeepSpaceLoaderProps {
  /** When provided, the bar is driven externally (sync mode). When omitted, internal timer runs. */
  progress?: number;
  /** Optional status label shown below "Synchronizing Systems" */
  status?: string;
}

export const DeepSpaceLoader: React.FC<DeepSpaceLoaderProps> = ({ progress: externalProgress, status }) => {
  const [cosmeticProgress, setCosmeticProgress] = useState(0);

  // Cosmetic count-up: 0% to 100%
  useEffect(() => {
    const target = externalProgress !== undefined ? externalProgress : 100;
    
    if (cosmeticProgress < target) {
      const timer = setTimeout(() => {
        // Smoothly move towards target: 2% every 20ms as requested
        setCosmeticProgress(prev => {
          const step = 2;
          const next = prev + step;
          return next >= target ? target : next;
        });
      }, 20);
      return () => clearTimeout(timer);
    }
  }, [externalProgress, cosmeticProgress]);

  return (
    <div 
      className="fixed inset-0 z-[var(--z-system)] flex flex-col items-center justify-center overflow-hidden bg-black"
    >
      {/* Dynamic Background Elements */}
      <motion.div 
        animate={{ 
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3] 
        }}
        transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-gradient-radial from-white/10 via-transparent to-transparent blur-[80px] rounded-full pointer-events-none" 
      />
      
      <div className="relative flex flex-col items-center">
        {/* Logo Container with enhanced animation */}
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="shadow-2xl mb-10 overflow-hidden shadow-indigo-600/40"
        >
          <Logo size={96} />
        </motion.div>
 
        {/* Text Area */}
        <div className="text-center space-y-4">
          <motion.h2 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="text-4xl font-black italic uppercase tracking-tighter px-8"
            style={{ color: 'white' }}
          >
            WholeScale <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400 pr-3">OS</span>
          </motion.h2>
          <div className="flex flex-col items-center gap-2">
            <div className="flex items-center gap-3 text-indigo-400/80 font-black uppercase tracking-[0.4em] text-[10px]">
              {status || 'Synchronizing Systems'}
            </div>
            <div className="text-2xl font-mono font-bold text-white/40 tabular-nums min-w-[120px]">
              <AnimatePresence mode="wait">
                {cosmeticProgress >= 100 ? (
                  <motion.span 
                    key="success"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-emerald-400 block"
                  >
                    Success!
                  </motion.span>
                ) : (
                  <motion.span
                    key="progress"
                    className="block transition-all duration-100 ease-out"
                  >
                    {Math.round(cosmeticProgress)}%
                  </motion.span>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
 
        {/* Progress Bar */}
        <div className="flex flex-col items-center mt-12">
          <div className="w-64 h-[3px] bg-white/5 rounded-full overflow-hidden relative border border-white/5">
            <div 
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 shadow-[0_0_20px_rgba(99,102,241,0.8)]"
              style={{ 
                width: `${Math.min(100, cosmeticProgress)}%`,
                transition: 'width 0.4s ease-out'
              }}
            />
          </div>
        </div>
      </div>

      {/* Decorative text */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.2 }}
        transition={{ delay: 2, duration: 2 }}
        className="absolute bottom-12 text-[10px] uppercase tracking-[0.8em] font-bold text-indigo-400"
      >
        Universal Operating System for Real Estate
      </motion.div>
    </div>
  );
};
